import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface Sucursal {
  id: string;
  gimnasio_id?: string;
  gym_nombre?: string;
  nombre: string;
  direccion?: string;
  telefono?: string;
  email?: string;
  capacidad_maxima?: number;
  estado?: string;
  horarios?: any;
  usuarios_activos?: number;
  fecha_apertura?: string;
}

interface SucursalContextType {
  sucursalActiva: Sucursal | null;
  sucursalActual: Sucursal | null;
  sucursales: Sucursal[];
  cambiarSucursal: (sucursal: Sucursal) => void;
  refreshSucursales: () => Promise<void>;
  loading: boolean;
}

const SucursalContext = createContext<SucursalContextType | undefined>(undefined);

export const SucursalProvider = ({ children }: { children: ReactNode }) => {
  const { profile } = useAuth();
  const [sucursalActiva, setSucursalActiva] = useState<Sucursal | null>(null);
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSucursales = async () => {
    if (!profile) {
      setSucursales([]);
      setSucursalActiva(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      if (profile.role !== 'platform_admin' && !profile.gimnasioId) {
        setSucursales([]);
        setSucursalActiva(null);
        setLoading(false);
        return;
      }

      let query = supabase
        .from('sucursales')
        .select('*, gimnasios(nombre)')
        .order('created_at');

      if (profile.role !== 'platform_admin' && profile.gimnasioId) {
        query = query.eq('gimnasio_id', profile.gimnasioId);
      }

      if (profile.sucursalId) {
        query = query.eq('id', profile.sucursalId);
      }

      const { data, error } = await query;

      if (error) throw error;

      const nextSucursales = (data || []).map((s: any) => ({
        ...s,
        gym_nombre: s?.gimnasios?.nombre || undefined,
      }));
      setSucursales(nextSucursales);

      setSucursalActiva((current) => {
        if (!nextSucursales.length) {
          return null;
        }

        if (current) {
          const stillExists = nextSucursales.find((s) => s.id === current.id);
          if (stillExists) return stillExists;
        }

        return nextSucursales[0];
      });
    } catch (error) {
      console.error('Error al cargar sucursales:', error);
      setSucursales([]);
      setSucursalActiva(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSucursales();
  }, [profile?.gimnasioId, profile?.role, profile?.id, profile?.sucursalId]);

  const cambiarSucursal = (sucursal: Sucursal) => {
    setSucursalActiva(sucursal);
  };

  return (
    <SucursalContext.Provider
      value={{
        sucursalActiva,
        sucursalActual: sucursalActiva,
        sucursales,
        cambiarSucursal,
        refreshSucursales: fetchSucursales,
        loading,
      }}
    >
      {children}
    </SucursalContext.Provider>
  );
};

export const useSucursal = () => {
  const context = useContext(SucursalContext);
  if (context === undefined) {
    throw new Error('useSucursal debe ser usado dentro de un SucursalProvider');
  }
  return context;
};
